import React, { useState, useEffect } from 'react';
import { db } from '../db';
import { Image as ImageIcon, Plus, ArrowLeft, X, Edit2, Trash2, Star, DownloadCloud, Loader2 } from 'lucide-react';
import MuscleIcon from '../components/MuscleIcons';
import { toast, confirmDialog } from '../utils/feedback';
import SEO from '../components/SEO';

const MUSCLE_CATEGORIES = [
  'Épaules', 'Biceps', 'Triceps', 'Avant-bras', 'Dos', 'Pectoraux', 
  'Abdos', 'Quadriceps', 'Ischio-jambiers', 'Mollets', 'Cou', 'Fessier', 'Callisthénie', 'Cardio'
];

const EQUIPMENT_TYPES = [
  'Haltères', 'Barre', 'Machine', 'Poulie', 'Kettlebell', 
  'Poids du corps', 'Bande de résistance', 'T-bar', 'Autre'
];

const POULIE_GRIPS = [
  { id: 'corde', name: 'Corde', img: '/grips/corde.png' },
  { id: 'barre_parallele', name: 'Barre poignées parallèles', img: '/grips/barre_parallele.png' },
  { id: 'barre_dorsaux', name: 'Barre à dorsaux', img: '/grips/barre_dorsaux.png' },
  { id: 'double_serree', name: 'Double poignée prise serrée', img: '/grips/double_serree.png' },
  { id: 'poignees_simples', name: 'Poignées simples', img: '/grips/poignees_simples.png' },
  { id: 'barre_v', name: 'Barre en V', img: '/grips/barre_v.png' },
  { id: 'barre_courte', name: 'Barre droite courte', img: 'https://www.fitandrack.com/web/image/product.template/1009/image_256' },
  { id: 'sangles_chevilles', name: 'Sangles de chevilles', img: 'https://www.metal-france.fr/Files/131735/Img/18/Sangles-chevilles-fitness-musculation-.jpg' }
];

const mapBodyPartToMuscle = (bodyPart, targetMuscle) => {
  const mapping = {
    'waist': 'Abdos', 'abs': 'Abdos', 'back': 'Dos', 'spine': 'Dos', 'lats': 'Dos',
    'chest': 'Pectoraux', 'pectorals': 'Pectoraux', 'shoulders': 'Épaules', 'delts': 'Épaules',
    'upper arms': 'Biceps', 'lower arms': 'Biceps', 'upper legs': 'Quadriceps', 'lower legs': 'Mollets',
    'cardio': 'Cardio', 'neck': 'Cou'
  };
  if (targetMuscle) {
    if (targetMuscle.includes('triceps')) return 'Triceps';
    if (targetMuscle.includes('biceps')) return 'Biceps';
    if (targetMuscle.includes('glutes')) return 'Fessier';
    if (targetMuscle.includes('quad')) return 'Quadriceps';
    if (targetMuscle.includes('hamstring')) return 'Ischio-jambiers';
  }
  return mapping[bodyPart] || 'Autre';
};

const mapEquipment = (equip) => {
  const mapping = {
    'body weight': 'Poids du corps', 'assisted': 'Poids du corps', 'barbell': 'Barre',
    'dumbbell': 'Haltères', 'cable': 'Poulie', 'machine': 'Machine'
  };
  return mapping[equip] || 'Autre';
};

const getEquipClass = (equip) => {
  if (!equip) return 'equip-Autre';
  return `equip-${equip.replace(/\s+/g, '-')}`;
};

const ExerciseImage = ({ exercise }) => {
  const [frame, setFrame] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (exercise.animationImages && exercise.animationImages.length > 1) {
      const interval = setInterval(() => {
        setFrame(prev => (prev + 1) % exercise.animationImages.length);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [exercise]);

  useEffect(() => {
    setLoading(true);
    setError(false);
  }, [exercise]);

  const hasMedia = Boolean(
    (exercise.animationImages && exercise.animationImages.length > 0) || 
    exercise.gifUrl || 
    exercise.videoUrl
  );

  if (hasMedia && !error) {
    let src = '';
    if (exercise.animationImages && exercise.animationImages.length > 0) {
      src = exercise.animationImages[frame];
    } else {
      src = exercise.gifUrl || exercise.videoUrl;
    }

    return (
      <div style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fff',
            zIndex: 1
          }}>
            <Loader2 size={24} color="var(--primary-color)" style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        )}
        <img 
          loading="lazy" 
          src={src} 
          alt={exercise.name} 
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover', 
            backgroundColor: '#fff',
            opacity: loading ? 0 : 1,
            transition: 'opacity 0.2s ease'
          }} 
          onLoad={() => setLoading(false)}
          onError={() => { setLoading(false); setError(true); }}
        />
      </div>
    );
  }

  const muscleType = exercise.muscles?.[0] || exercise.muscle || 'Autre';

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.35rem',
      background: 'linear-gradient(135deg, rgba(140, 127, 191, 0.12) 0%, rgba(110, 181, 131, 0.08) 100%)',
      padding: '0.5rem'
    }}>
      <MuscleIcon type={muscleType} size={46} />
      <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 500, maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {muscleType !== 'Autre' ? muscleType : (exercise.equipment || 'Exercice')}
      </span>
    </div>
  );
};


const PreviewImage = ({ src }) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    setError(false);
  }, [src]);

  return (
    <div style={{ marginTop: '0.5rem', width: '100%', maxWidth: '250px', aspectRatio: '1 / 1', borderRadius: 'var(--border-radius-sm)', overflow: 'hidden', border: '1px solid var(--border-color)', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: error ? 'var(--bg-color)' : '#fff', margin: '0.5rem auto', position: 'relative' }}>
      {loading && !error && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg-color)' }}>
          <Loader2 size={32} color="var(--primary-color)" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      )}
      {!error ? (
        <img 
          src={src} 
          alt="Aperçu" 
          style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: loading ? 0 : 1, transition: 'opacity 0.3s' }} 
          onLoad={() => setLoading(false)}
          onError={() => { setLoading(false); setError(true); }}
        />
      ) : (
        <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '1rem' }}>
          Lien invalide ou image introuvable.
        </div>
      )}
    </div>
  );
};


const EditExerciseModal = ({ exercise, onClose, onSave, onDelete }) => {
  const [name, setName] = useState(exercise.name);
  const [muscles, setMuscles] = useState(Array.isArray(exercise.muscles) ? exercise.muscles : (exercise.muscle ? [exercise.muscle] : []));
  const [equipment, setEquipment] = useState(exercise.equipment || 'Autre');
  const [emptyWeight, setEmptyWeight] = useState(exercise.emptyWeight || '');
  const [grip, setGrip] = useState(exercise.grip || '');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(exercise.gifUrl || (exercise.animationImages && exercise.animationImages.length > 0 ? exercise.animationImages[0] : ''));


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (muscles.length === 0) {
      toast.error("Sélectionne au moins un muscle");
      return;
    }
    setSaving(true);
    
    try {
      let finalUrl = preview;
      let animImages = exercise.animationImages || [];

      // Astuce magique : si l'utilisateur donne un lien finissant par 0.jpg (venant de la base open-source)
      // On crée automatiquement l'animation avec 0.jpg et 1.jpg !
      if (finalUrl && finalUrl.endsWith('0.jpg')) {
        animImages = [finalUrl, finalUrl.replace('0.jpg', '1.jpg')];
        finalUrl = ''; // On vide le lien gifUrl car on gère l'animation nous-mêmes
      } else if (finalUrl && finalUrl.endsWith('1.jpg')) {
        animImages = [finalUrl.replace('1.jpg', '0.jpg'), finalUrl];
        finalUrl = ''; 
      }

      const updated = {
        ...exercise,
        name,
        muscles,
        muscle: muscles[0] || 'Autre',
        equipment,
        emptyWeight: equipment === 'Machine' ? emptyWeight : '',
        grip: equipment === 'Poulie' ? grip : '',
        gifUrl: finalUrl,
        animationImages: animImages
      };
      
      const profileId = localStorage.getItem('fitTrackProfileId');
      if (exercise.id) {
        await db.updateExercise(updated, profileId);
      } else {
        await db.addExercise(updated, profileId);
      }
      
      setSaving(false);
      onSave(updated);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'enregistrement : " + err.message);
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!exercise.id) return;
    confirmDialog("Es-tu sûr de vouloir supprimer cet exercice ?", async () => {
      const profileId = localStorage.getItem('fitTrackProfileId');
      await db.deleteExercise(exercise, profileId);
      toast.success("Exercice supprimé avec succès.");
      onDelete();
    });
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(5px)' }}>
      <div className="card" style={{ width: '90%', maxWidth: '450px', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
        <div className="flex justify-between items-center" style={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0 }}>{exercise.id ? "Modifier l'Exercice" : "Nouvel Exercice"}</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '0.5rem', borderRadius: '50%', display: 'flex' }}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>

          <div className="form-group">
            <label className="form-label">Nom de l'exercice</label>
            <input type="text" className="form-control" value={name} onChange={e => setName(e.target.value)} required />
          </div>

          <div className="form-group">
            <label className="form-label">URL du GIF ou Vidéo (Optionnel)</label>
            <input type="url" className="form-control" placeholder="https://..." value={preview} onChange={e => setPreview(e.target.value)} />
            {preview && <PreviewImage src={preview} />}
          </div>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label className="form-label">Catégorie(s)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', maxHeight: '150px', overflowY: 'auto', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius-sm)' }}>
                {Array.from(new Set([...muscles, ...MUSCLE_CATEGORIES])).filter(c => c !== 'Jambes').map(c => {
                  const isSelected = muscles.includes(c);
                  const isPrimary = muscles.indexOf(c) === 0;
                  
                  let bgColor = 'var(--bg-color)';
                  let textColor = 'var(--text-secondary)';
                  let borderColor = 'var(--border-color)';
                  let badgeColor = '';
                  
                  if (isSelected) {
                    if (isPrimary) {
                      textColor = 'var(--primary-color)';
                      borderColor = 'var(--primary-color)';
                      badgeColor = 'var(--primary-color)';
                    } else {
                      textColor = 'var(--secondary-color)';
                      borderColor = 'var(--secondary-color)';
                      badgeColor = 'var(--secondary-color)';
                    }
                  }

                  return (
                    <label 
                      key={c}
                      title={isSelected ? (isPrimary ? "Muscle Primaire" : "Muscle Secondaire") : ""}
                      style={{
                        position: 'relative',
                        padding: '0.4rem 0.8rem',
                        borderRadius: '20px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        transition: 'all 0.2s',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        backgroundColor: bgColor,
                        color: textColor,
                        border: `1px solid ${borderColor}`,
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <input 
                        type="checkbox"
                        style={{ display: 'none' }}
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) setMuscles([...muscles, c]);
                          else setMuscles(muscles.filter(m => m !== c));
                        }}
                      />
                      {c}
                      {isSelected && (
                        <span style={{
                          position: 'absolute',
                          top: '-8px',
                          left: '-10px',
                          backgroundColor: badgeColor,
                          color: '#fff',
                          fontSize: '0.65rem',
                          padding: '2px 5px',
                          borderRadius: '10px',
                          fontWeight: 'bold',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}>
                          {isPrimary ? '1er' : '2nd'}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
            
            <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label className="form-label">Équipement</label>
              <select className="form-control" value={equipment} onChange={e => setEquipment(e.target.value)}>
                {EQUIPMENT_TYPES.map(eq => <option key={eq} value={eq}>{eq}</option>)}
              </select>
            </div>
            {(equipment === 'Machine') && (
              <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                <label className="form-label">Poids à vide (kg)</label>
                <input type="number" step="0.5" className="form-control" placeholder="Ex: 75" value={emptyWeight} onChange={e => setEmptyWeight(e.target.value)} />
              </div>
            )}

            {(equipment === 'Poulie') && (
              <div className="form-group" style={{ flex: '1 1 100%', marginTop: '0.5rem', marginBottom: 0 }}>
                <label className="form-label mb-2">Accessoire / Prise</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '10px' }}>
                  {POULIE_GRIPS.map(g => (
                    <div 
                      key={g.id}
                      onClick={() => setGrip(g.id)}
                      style={{ 
                        border: grip === g.id ? '2px solid var(--accent-color)' : '1px solid var(--border-color)', 
                        borderRadius: 'var(--border-radius-sm)', 
                        padding: '5px', 
                        cursor: 'pointer', 
                        textAlign: 'center',
                        backgroundColor: grip === g.id ? 'rgba(var(--accent-color-rgb), 0.1)' : 'transparent',
                        transition: 'all 0.2s'
                      }}>
                      <div style={{ height: '80px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff', borderRadius: '4px', overflow: 'hidden' }}>
                        <img loading="lazy" src={g.img} alt={g.name} style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', transform: 'scale(1.15)' }} />
                      </div>
                      <div style={{ fontSize: '0.8rem', lineHeight: '1.2', fontWeight: grip === g.id ? 'bold' : 'normal' }}>{g.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex gap-2 mt-4">
            <button type="submit" className="btn btn-primary" style={{ flex: 2 }} disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            {exercise.id && (
              <button type="button" className="btn btn-danger" style={{ flex: 1 }} onClick={handleDelete}>
                <Trash2 size={18} />
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const ExerciseLibrary = ({ currentProfile }) => {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editingEx, setEditingEx] = useState(null);
  const [exercisePRs, setExercisePRs] = useState({});
  const [exercise1RMs, setExercise1RMs] = useState({});

  const favs = currentProfile?.favoriteExercises || [];

  const toggleFavorite = async (exId, e) => {
    e.stopPropagation();
    if (!currentProfile) return;
    
    let newFavs = [...favs];
    if (newFavs.includes(exId)) {
      newFavs = newFavs.filter(id => id !== exId);
    } else {
      newFavs.push(exId);
    }
    
    await db.updateProfile({ ...currentProfile, favoriteExercises: newFavs });
  };

  const loadExercises = async () => {
    setLoading(true);
    const profileId = localStorage.getItem('fitTrackProfileId');
    
    const [eData, wData] = await Promise.all([
      db.getExercises(profileId),
      profileId ? db.getWorkouts(profileId) : Promise.resolve([])
    ]);
    
    setExercises(eData);

    const maxWeights = {};
    const rms = {};

    wData.forEach(workout => {
      workout.exercises.forEach(block => {
        const items = block.type === 'superset' ? block.items : [block];
        items.forEach(ex => {
          let maxWeightInWorkout = 0;
          
          if (ex.sets) {
            ex.sets.forEach(set => {
              const weight = parseFloat(set.weight);
              const reps = parseInt(set.reps);
              if (weight > 0 && reps > 0) {
                if (weight > maxWeightInWorkout) maxWeightInWorkout = weight;
                
                const current1RM = weight * (1 + reps / 30);
                if (!rms[ex.exerciseId] || current1RM > rms[ex.exerciseId]) {
                  rms[ex.exerciseId] = Math.round(current1RM);
                }
              }
            });
          }

          if (maxWeightInWorkout > 0) {
            if (!maxWeights[ex.exerciseId] || maxWeightInWorkout > maxWeights[ex.exerciseId]) {
              maxWeights[ex.exerciseId] = maxWeightInWorkout;
            }
          }
        });
      });
    });

    setExercisePRs(maxWeights);
    setExercise1RMs(rms);
    
    setLoading(false);
  };

  const fetchExercises = async () => {
    setLoading(true);
    const profileId = localStorage.getItem('fitTrackProfileId');
    try {
      const data = await db.getExercises(profileId);
      setExercises(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchExercises();
  }, []);



  if (loading) return <div className="text-center mt-3">Chargement...</div>;

  // VIEW 1: Categories
  const getMuscles = (ex) => {
    let rawMuscles = ex.muscles || (ex.muscle ? [ex.muscle] : null);
    if (!rawMuscles) return ['Autre'];
    const arr = Array.isArray(rawMuscles) ? rawMuscles : [rawMuscles];
    return arr.map(m => {
      if (m === 'Jambes') {
        const lowerName = (ex.name || '').toLowerCase();
        if (/(curl|ischio|soulevé|souleve|deadlift|sdt|good morning|rdl|arrière cuisse|arriere cuisse)/.test(lowerName)) return 'Ischio-jambiers';
        if (/(hip thrust|fessier|glute|abduct)/.test(lowerName)) return 'Fessier';
        return 'Quadriceps';
      }
      return m;
    });
  };
  
  const availableCategories = Array.from(new Set([
    ...MUSCLE_CATEGORIES,
    ...exercises.flatMap(e => getMuscles(e))
  ])).filter(cat => cat !== 'Autre' && cat !== 'Bras' && cat !== 'Dos inférieur' && cat !== 'Triceps sural' && cat !== 'Abdominaux' && cat !== 'Jambes');

  if (!selectedCategory) {
    return (
      <div>
        <SEO title="Bibliothèque d'Exercices" />
        <div className="categories-grid">
          <div 
            className="card" 
            style={{ cursor: 'pointer', textAlign: 'center', padding: '1.5rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', border: '2px solid var(--secondary-color)' }}
            onClick={() => setSelectedCategory('Favoris')}
          >
            <Star size={64} color="var(--secondary-color)" fill="var(--secondary-color)" />
            <span style={{ fontWeight: 'bold' }}>⭐ Favoris</span>
          </div>
          {availableCategories.map(cat => (
            <div 
              key={cat} 
              className="card" 
              style={{ cursor: 'pointer', textAlign: 'center', padding: '1.5rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}
              onClick={() => setSelectedCategory(cat)}
            >
              <MuscleIcon type={cat} size={64} />
              <span style={{ fontWeight: 'bold' }}>{cat}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const filteredExercises = exercises.filter(e => {
    if (selectedCategory === 'Favoris') return favs.includes(e.id);
    const m = getMuscles(e);
    if (selectedCategory === 'Biceps' && m.includes('Bras') && e.name.toLowerCase().includes('curl')) return true;
    if (selectedCategory === 'Triceps' && m.includes('Bras') && (e.name.toLowerCase().includes('triceps') || e.name.toLowerCase().includes('front'))) return true;
    return m.includes(selectedCategory);
  });

  return (
    <div>
      <SEO title={`Exercices - ${selectedCategory}`} />
      <div className="flex justify-between items-center mb-3">
        <button 
          onClick={() => setSelectedCategory(null)} 
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', cursor: 'pointer', padding: '0.5rem 1rem', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: 'var(--shadow)', fontWeight: 'bold' }}
        >
          <ArrowLeft size={18} /> {selectedCategory}
        </button>
          <div className="flex gap-2">
            <button className="btn btn-primary" onClick={() => setEditingEx({ name: '', muscles: [selectedCategory], equipment: 'Autre', gifUrl: '' })}>
              <Plus size={20} /> Ajouter
            </button>
          </div>
      </div>

      {/* Equipment Legend */}
      <div className="card mb-3" style={{ padding: '0.8rem', display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
        {EQUIPMENT_TYPES.map(eq => (
          <div key={eq} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
            <span className={`equip-dot ${getEquipClass(eq)}`}></span>
            <span>{eq}</span>
          </div>
        ))}
      </div>

      <div className="exercises-list-grid" style={{ gap: '1rem' }}>
        {filteredExercises.length === 0 && <p style={{ color: 'var(--text-muted)', gridColumn: '1 / -1' }}>
          {selectedCategory === 'Callisthénie' ? (
            <>C'est pas notre fort la callisthénie,<br />on l'avoue. Ajoute tes propres exos&nbsp;!</>
          ) : (
            "Aucun exercice dans cette catégorie."
          )}
        </p>}
        {filteredExercises.map(ex => (
          <div key={ex.id} className="card" style={{ display: 'flex', flexDirection: 'column', cursor: 'pointer', position: 'relative', padding: '1rem' }} onClick={() => setEditingEx(ex)}>
            
            {/* Favorite Star */}
            <div 
              style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', zIndex: 10, cursor: 'pointer', background: 'rgba(0,0,0,0.5)', borderRadius: '50%', padding: '0.3rem', display: 'flex', transition: 'all 0.2s' }}
              onClick={(e) => toggleFavorite(ex.id, e)}
            >
              <Star 
                size={20} 
                color={favs.includes(ex.id) ? "var(--secondary-color)" : "#fff"} 
                fill={favs.includes(ex.id) ? "var(--secondary-color)" : "transparent"} 
              />
            </div>
            
            {/* Colored Equipment Dot */}
            <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', zIndex: 10 }}>
              <span className={`equip-dot ${getEquipClass(ex.equipment)}`} title={ex.equipment || 'Autre'}></span>
            </div>

            <div style={{ position: 'relative', width: '100%', paddingTop: '100%', backgroundColor: 'var(--bg-color)', borderRadius: 'var(--border-radius-sm)', marginBottom: '1rem', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border-color)' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ExerciseImage exercise={ex} />
              </div>
              <div style={{ position: 'absolute', bottom: '0.5rem', right: '0.5rem', backgroundColor: 'rgba(0,0,0,0.6)', padding: '0.4rem', borderRadius: '50%', display: 'flex' }}>
                <Edit2 size={14} color="#fff" />
              </div>
            </div>
            
            <div className="flex justify-between items-center mt-auto">
              <span style={{ fontWeight: 'bold', fontSize: '1rem' }}>
                {ex.name}{ex.equipment && ex.equipment !== 'Autre' ? <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal', fontSize: '0.9em' }}> ({ex.equipment})</span> : ''}
              </span>
            </div>
            
            <div style={{ display: 'flex', gap: '0.8rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              {exercisePRs[ex.id] && (
                <div style={{ fontSize: '0.85rem', color: 'var(--secondary-color)', fontWeight: 'bold' }}>
                  🏅 PR : {exercisePRs[ex.id]} kg
                </div>
              )}
              {exercise1RMs[ex.id] && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>
                  🏆 1RM : {exercise1RMs[ex.id]} kg
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {editingEx && (
        <EditExerciseModal 
          exercise={editingEx} 
          onClose={() => setEditingEx(null)}
          onSave={(updated) => {
            setEditingEx(null);
            loadExercises();
          }}
          onDelete={() => {
            setEditingEx(null);
            loadExercises();
          }}
        />
      )}
    </div>
  );
};

export default ExerciseLibrary;
